import {
  Box,
  Stack,
  Typography,
  Chip,
  MenuItem,
  Checkbox,
  ListItemText,
  Select,
} from "@mui/material";
import { useMemo, useState, useEffect } from "react";
import { useTheme } from "@emotion/react";
import { useSelector } from "react-redux";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import {
  CheckCircle,
  CreditCard,
  Mail,
  VpnKey,
} from "@mui/icons-material";
import dayjs from "dayjs";
import relativeTime from "dayjs/plugin/relativeTime";

import { BackArrow } from "../../assets";
import { Wrapper } from "../wrapper";
import {
  RecentActivitiesTable,
  RecentApiCallTable,
} from "../table";
import { viewLogs, dashboardRecentActiviy, dropDowndata, viewLogsV2 } from "../../services";
import { formatDateToDMY } from "../../utils";
import FilterAltOutlinedIcon from "@mui/icons-material/FilterAltOutlined";
import { OutlinedInput, InputAdornment } from "@mui/material";
import RadioButtonUncheckedIcon from "@mui/icons-material/RadioButtonUnchecked";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import CheckBoxOutlineBlankIcon from "@mui/icons-material/CheckBoxOutlineBlank";
import CheckBoxIcon from "@mui/icons-material/CheckBox";
import { Loader } from "../loader";


dayjs.extend(relativeTime);

export const DetailedLogs = () => {
  const location = useLocation();
  const { date: dateParam } = useParams();
  const { selectedApiId, selectedCategory, showRecentActivities } = location.state || {};
  const date = dateParam || location.state?.date;
  const isDetailedLogsPage = location.pathname.includes('/access-logs/') && location.pathname.includes('/detailed-logs');

  const theme = useTheme();
  const { user } = useSelector((state) => state.auth);
  const jwtToken = user?.jwt;
  const [logData, setLogData] = useState([]);
  const [logDataCount, setLogDataCount] = useState(0);
  const [detailedLogData, setDetailedLogData] = useState([]);
  const [detailedLogDataCount, setDetailedLogDataCount] = useState(0);
  const [showDetailedLogs, setShowDetailedLogs] = useState(false);
  const [recentActivities, setRecentActivities] = useState([]);
  const [dropDownData, setDropDownData] = useState([]);
  const [recentActivitiesCount, setRecentActivitiesCount] = useState(0);
  const navigate = useNavigate();

  const [page, setPage] = useState(1);
  const [rowsPerPageLogs, setRowsPerPageLogs] = useState(15);
  const [rowsPerPageRecent, setRowsPerPageRecent] = useState(8);

  /* ----------------------- FILTER STATE ----------------------- */
  const [selectedApiTypes, setSelectedApiTypes] = useState([]);
  const [selectedStatusCodes, setSelectedStatusCodes] = useState([]);
  const [loading, setLoading] = useState(true);

  const handleApiNameClick = (apiName) => {
    setSelectedApiTypes([apiName]);
    setPage(1);
    navigate(`/usage-reports/access-logs/${date}/detailed-logs`, {
      state: {
        selectedApiId,
        selectedCategory,
      },
    });
  };

  /* ----------------------- CHECK URL AND SET DETAILED LOGS STATE ----------------------- */
  useEffect(() => {
    setShowDetailedLogs(isDetailedLogsPage);
    if (!isDetailedLogsPage) {
      setSelectedApiTypes([]);
    }
  }, [isDetailedLogsPage]);

  /* ----------------------- API CALL ----------------------- */
  useEffect(() => {
    const fetchDropDownData = async () => {
      if (!showRecentActivities) {
        try {
        const res = await dropDowndata({
          filterDate: date,
          token: jwtToken,
          id: selectedCategory === 'all' ? null : selectedApiId,
        });
        setDropDownData(res?.data || []);
        } catch (error) {
          console.error('Error fetching logs:', error);
        }
      }
    };

    if (date || !showRecentActivities) {
      fetchDropDownData();
    }
  }, [date, jwtToken]);

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true); 
  
      try {
        if (showRecentActivities) {
          const res = await dashboardRecentActiviy(jwtToken);
          if (res?.status === 200) {
            setRecentActivities(res?.data?.data || []);
            setRecentActivitiesCount(res?.data?.data.length);
          }
        } else if (showDetailedLogs) {
          const res = await viewLogsV2({
            filterDate: date,
            apiName: selectedApiTypes.length ? selectedApiTypes.join(", ") : undefined,
            statusCode: selectedStatusCodes.length ? selectedStatusCodes.join(", ") : undefined,
            token: jwtToken,
            page,
            pageSize: rowsPerPageLogs,
            id: selectedCategory === "all" ? null : selectedApiId,
          });
  
          setDetailedLogData(res?.data?.data || []);
          setDetailedLogDataCount(res?.data?.pagination?.totalRecords);
        } else {
          const res = await viewLogs({
            filterDate: date,
            apiName: selectedApiTypes.length ? selectedApiTypes.join(", ") : undefined,
            statusCode: selectedStatusCodes.length ? selectedStatusCodes.join(", ") : undefined,
            token: jwtToken,
            page,
            pageSize: rowsPerPageLogs,
            id: selectedCategory === "all" ? null : selectedApiId,
          });
  
          setLogData(res?.data?.data || []);
          setLogDataCount(res?.data?.pagination?.totalRecords);
        }
      } catch (error) {
        console.error("Error fetching logs:", error);
      } finally {
        setLoading(false);
      }
    };
  
    if (date || showRecentActivities) {
      fetchData();
    }
  }, [date, jwtToken, selectedApiId, selectedCategory, showRecentActivities, showDetailedLogs, page, rowsPerPageLogs, selectedApiTypes, selectedStatusCodes,]);
  
  const getActivityIcon = (type) => {
    switch (type) {
      case 'api_call':
        return <CheckCircle sx={{ fontSize: 16, color: '#10b981' }} />;
      case 'credit_purchase':
        return <CreditCard sx={{ fontSize: 16, color: '#f59e0b' }} />;
      case 'support_mail':
        return <Mail sx={{ fontSize: 16, color: '#3b82f6' }} />;
      case 'key_generation':
        return <VpnKey sx={{ fontSize: 16, color: '#8b5cf6' }} />;
      default:
        return <CheckCircle sx={{ fontSize: 16, color: '#64748b' }} />;
    }
  };

  const getActivityStatus = (type, metadata) => {
    if (type === 'api_call') {
      const status = metadata?.statusCode === 200 ? 'Success' : 'Failed';
      const color = metadata?.statusCode === 200 ? '#10b981' : '#ef4444';
      return <Chip label={status} size="small" sx={{ backgroundColor: `${color}15`, color }} />;
    }
    return <Chip label="Success" size="small" sx={{ backgroundColor: '#10b98115', color: '#10b981' }} />;
  };
 

  const convertRecentActivitiesData = (data) => {
    return data?.map((item, index) => ({
      key: index.toString(),
      type: (
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: 1,
          }}
        >
          {getActivityIcon(item.type)}
          <Typography
            sx={{
              textTransform: 'capitalize',
              fontSize: '14px',
              fontFamily: 'Inter',
              fontWeight: 400,
            }}
          >
            {item.type.replace('_', ' ')}
          </Typography>
        </Box>
      ),
      description: (
        <Typography sx={{ fontSize: '14px', fontFamily: 'Inter', fontWeight: 400 }}>
          {item.description}
        </Typography>
      ),
      timestamp: (
        <Typography sx={{ fontSize: '14px', fontFamily: 'Inter', fontWeight: 400 }}>
          {dayjs(item.timestamp).format('MMM DD, YYYY HH:mm:ss')}
        </Typography>
      ),
      status: getActivityStatus(item.type, item.metadata),
      metadata: (
        <Typography sx={{ fontSize: '14px', fontFamily: 'Inter', fontWeight: 400 }}>
          {item.metadata?.apiName || item.metadata?.keyPrefix || item.metadata?.amount || '-'}
        </Typography>
      ),
    }));
  };
  
  const convertDetailedData = (data) => {
    if (data.message === 'No data found for the specified date.') {
      return null;
    }
    return data?.map((item, index) => ({
      key: index.toString(),
      apiName: item?.apiName,
      requestTime: formatDateToDMY(item?.requestTime, true),
      responseTime: item?.responseTime,
      httpStatus: item?.httpStatus,
      status: (
        <Box
          component="span"
          color={item.status === 'Fetched' ? 'green' : '#FF4A4A'}
        >
          {item.status}
        </Box>
      ),
      sequenceID: (
        <Box
          component="span"
        >
          {item?.requestId}
        </Box>
      ),
      creditUtilized: item?.totalConsumedCredit,
    }));
  };

  const convertData = (data) => {
    if (data.message === 'No data found for the specified date.') {
      return null;
    }
    return data?.map((item, index) => ({
      key: index.toString(),
      apiName: item?.api_name,
      requestTime: item?.total_request,
      responseTime: `${item?.success_rate}%`,
      httpStatus: item?.success_count,
      failedCount: item?.failure_count,
      status: (
        <Box
          component="span"
          color={item?.failure_count === '0' ? 'green' : '#FF4A4A'}
        >
          {item?.failure_count === '0' ? 'Success' : 'Failed'}
        </Box>
      ),
      sequenceID: (
        <Box
          component="span"
        >
          -
        </Box>
      ),
      creditUtilized: item?.credit_utilized,
    }));
  };

  const data = useMemo(() => {
    if (showRecentActivities) {
      return convertRecentActivitiesData(recentActivities);
    }
    if (showDetailedLogs) {
      return convertDetailedData(detailedLogData);
    }
    return convertData(logData);
  }, [logData, detailedLogData, recentActivities, showRecentActivities, showDetailedLogs]);

  const paginatedRecentActivities = useMemo(() => {
    if (!showRecentActivities) return [];
  
    const startIndex = (page - 1) * rowsPerPageRecent;
    const endIndex = startIndex + rowsPerPageRecent;
  
    return convertRecentActivitiesData(
      recentActivities.slice(startIndex, endIndex)
    );
  }, [recentActivities, page, rowsPerPageRecent, showRecentActivities]);
  
  useEffect(() => {
    setPage(1);
  }, [rowsPerPageLogs, rowsPerPageRecent]);

   /* ----------------------- COMMON SELECT STYLE ----------------------- */
   const commonSelectSx = {
    borderRadius: "8px",
    width: 220,
    height: 38,
    bgcolor: "#fff",
  };

  /* ----------------------- UI ----------------------- */
  
  return (
    <Box>
      <Wrapper sx={{ paddingTop: theme.spacing(8) }}>
       {loading && <Loader />}
        <Stack
          direction="row"
          alignItems="center"
          justifyContent="space-between"
          sx={{ pt: 2, pb: 2 }}
        >
        {/* Left: Back + Title */}
          <Stack direction="row" alignItems="center" spacing={2}>
            <Box
            onClick={() => {
              if (showRecentActivities) {
                navigate("/");
              } else if (isDetailedLogsPage) {
                navigate(`/usage-reports/access-logs/${date}`, {
                  state: {
                    selectedApiId,
                    selectedCategory,
                  },
                });
              } else {
                navigate("/usage-reports");
              }
            }}
              component="img"
              sx={{ cursor: "pointer" }}
            src={BackArrow}
            />

          <Typography
            sx={{ color: theme.palette.textColor.primary }}
            variant="h4"
          >
              {isDetailedLogsPage ? "Detailed Logs" : "View Logs"}
            </Typography>
          </Stack>

        {/* Right: Search */}
          {!showRecentActivities && (
            <Stack direction="row" spacing={2}>
              {/* API TYPE */}
              <Select
                multiple
                displayEmpty
                value={selectedApiTypes}
                size="small"
                sx={commonSelectSx}
                onChange={(e) => {
                  setSelectedApiTypes(e.target.value);
                  setPage(1);
                }}
                renderValue={(selected) =>
                  selected.length ? selected.join(", ") : "API Type"
                }
                input={
                  <OutlinedInput
                    startAdornment={
                      <InputAdornment position="start">
                        <FilterAltOutlinedIcon sx={{ fontSize: 18 }} />
                      </InputAdornment>
                    }
                  />
                }
                MenuProps={{
                  anchorOrigin: {
                    vertical: 'bottom',
                    horizontal: 'left',
                  },
                  transformOrigin: {
                    vertical: 'top',
                    horizontal: 'left',
                  },
                  PaperProps: {
                    sx: { width: 330, borderRadius: 2 },
                  },
                }}
              >
                {/* 🔹 Header with Clear */}
                <MenuItem
                  disableRipple
                  sx={{
                    position: "sticky",
                    top: 0,
                    zIndex: 1,
                    bgcolor: "#fff",
                    display: "flex",
                    justifyContent: "space-between",
                    cursor: "default",
                    "&:hover": { bgcolor: "transparent" },
                  }}
                >
                  <Typography fontWeight={600}>Select API Types</Typography>

                  {selectedApiTypes.length > 0 && (
                    <Typography
                      sx={{
                        fontSize: 12,
                        color: "primary.main",
                        cursor: "pointer",
                      }}
                      onClick={(e) => {
                        e.stopPropagation(); // 🔑 prevent menu close
                        setSelectedApiTypes([]);
                        setPage(1);
                      }}
                    >
                      Clear
                    </Typography>
                  )}
                </MenuItem>

                {/* 🔹 Options */}
                {Array.isArray(dropDownData?.apiNames) &&
                 dropDownData.apiNames.map((type) => (
                  <MenuItem key={type} value={type}>
                    <Checkbox
                      checked={selectedApiTypes.includes(type)}
                      icon={<CheckBoxOutlineBlankIcon />}
                      checkedIcon={<CheckBoxIcon />}
                      sx={{
                        "& .MuiSvgIcon-root": {
                          fontSize: 22,
                        },
                        p: "4px",
                      }}
                    />
                    <ListItemText primary={type} />
                  </MenuItem>
                ))}
              </Select>

              {/* STATUS CODE */}
              <Select
              multiple
              displayEmpty
              value={selectedStatusCodes}
              size="small"
              sx={commonSelectSx}
              onChange={(e) => {
                setSelectedStatusCodes(e.target.value);
                setPage(1);
              }}
              renderValue={(selected) =>
                selected.length ? selected.join(", ") : "Status Code"
              }
              input={
                <OutlinedInput
                  startAdornment={
                    <InputAdornment position="start">
                      <FilterAltOutlinedIcon sx={{ fontSize: 18 }} />
                    </InputAdornment>
                  }
                />
              }
              MenuProps={{
                anchorOrigin: {
                  vertical: 'bottom',
                  horizontal: 'left',
                },
                transformOrigin: {
                  vertical: 'top',
                  horizontal: 'left',
                },
                PaperProps: {
                  sx: { width: 220, borderRadius: 2 },
                },
              }}
            >
              {/* 🔹 Header with Clear */}
              <MenuItem
                disableRipple
                sx={{
                  position: "sticky",
                  top: 0,
                  zIndex: 1,
                  bgcolor: "#fff",
                  display: "flex",
                  justifyContent: "space-between",
                  cursor: "default",
                  "&:hover": { bgcolor: "transparent" },
                }}
              >
                <Typography fontWeight={600}>Select Status Codes</Typography>

                {selectedStatusCodes.length > 0 && (
                  <Typography
                    sx={{
                      fontSize: 12,
                      color: "primary.main",
                      cursor: "pointer",
                    }}
                    onClick={(e) => {
                      e.stopPropagation(); // prevent menu close
                      setSelectedStatusCodes([]);
                      setPage(1);
                    }}
                  >
                    Clear
                  </Typography>
                )}
              </MenuItem>

              {/* 🔹 Options */}
              {Array.isArray(dropDownData?.statusCodes) &&
                dropDownData.statusCodes.map((code) => (
                <MenuItem key={code} value={code}>
                  <Checkbox
                    checked={selectedStatusCodes.includes(code)}
                    icon={<CheckBoxOutlineBlankIcon />}
                    checkedIcon={<CheckBoxIcon />}
                    sx={{
                      "& .MuiSvgIcon-root": {
                        fontSize: 22,
                      },
                      p: "4px",
                    }}
                  />
                  <ListItemText primary={code} />
                </MenuItem>
              ))}
            </Select>
            </Stack>
          )}
        </Stack>

        {showRecentActivities ? (
          <RecentApiCallTable
            data={paginatedRecentActivities}
            totalCount={recentActivities.length}
            page={page}
            rowsPerPage={rowsPerPageRecent}
            onPageChange={setPage}
            onRowsPerPageChange={(value) => {
            setRowsPerPageRecent(value);
            setPage(1);
          }}
          />
        ) : (
          <RecentActivitiesTable
            data={data}
            totalCount={showDetailedLogs ? detailedLogDataCount : logDataCount}
            page={page}
            rowsPerPage={rowsPerPageLogs}
            onPageChange={setPage}
            onRowsPerPageChange={setRowsPerPageLogs}
            onApiNameClick={handleApiNameClick}
            showDetailedView={showDetailedLogs}
          />
        )}
      </Wrapper>
      <Box sx={{ mt: 1 }}> </Box>
    </Box>
  );
};